// ═══════════════════════════════════════════════════════════════════
// plazaShell3D.jsx — THE PLAZA'S STONE, BUILT IN BLENDER   (caP'n, 2026-09-22)
// ═══════════════════════════════════════════════════════════════════
// `public/models/plaza_shell.glb` is the Plaza's FIXED architecture only:
// the floor, the north wall with its base band, pilasters and two-step
// cornice, the portal shelf, the capped side walls with their posts, the
// south plinth and iron balustrade, and the slab the hall hangs from.
// Builder: gamemaster/models/sculpt/builders/plaza_shell_blender/.
//
// ⭐ NOTHING THAT MOVES IS IN THE GLB. Kiosks, stalls, the fountain, the
// portals, the people and every sign stay live JSX, placed by plazaZones —
// so moving a kiosk is still a plazaZones.js edit and never a rebuild
// (caP'n: "whatever's easier to edit later"). Only the shell's own ambient
// occlusion is baked, never a fixture's, for the same reason.
//
// The glb carries a grey AO atlas on UV1; the STONE is two 4 m seamless
// tiles (colour + relief) off plaza_1.webp on UV0 — the painting's big
// charcoal blocks, not the primitive room's small purple bricks
// (caP'n, 2026-09-22: "another real deal pass"). <Room/> keeps its bricks.
//
// THE FAIL-SAFE LAW: <Room/> (plazaPieces3D) stands in while the glb loads,
// if it fails, when the Plaza can't keep up (sculptGate), and when the glb
// was built for a different Plaza size than the one running (its extras
// carry cols/rows — change the map without a rebuild and it just falls back).
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useMemo } from "react";
import { useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Room, STONE, IRON, WALL_N, WALL_E, WALL_S, SHELF_ROWS } from "./plazaPieces3D";
import { usePaintedBodies } from "./sculptGate";

export const PLAZA_SHELL_URL = `${process.env.PUBLIC_URL || ""}/models/plaza_shell.glb`;
// the stone itself: 4 m seamless tiles off plaza_1.webp (make_plaza_tiles.py beside the shell's builder)
const TEX = `${process.env.PUBLIC_URL || ""}/models/tex/`;
export const STONE_TILES = ["plaza_floor_col.jpg", "plaza_floor_nrm.jpg", "plaza_wall_col.jpg", "plaza_wall_nrm.jpg"].map((f) => TEX + f);

// a stone tile on the glb's UV0 (already in 4 m repeats)
const tiled = (t, color) => {
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.flipY = false;                    // glTF UVs run top-down
  t.colorSpace = color ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.anisotropy = 8; t.channel = 0; t.needsUpdate = true;
  return t;
};

function readDims(scene) {
  let d = null;
  scene.traverse((o) => { if (!d && o.userData && o.userData.ie_plaza) { try { d = JSON.parse(o.userData.ie_plaza); } catch (e) { d = null; } } });
  return d;
}
export const shellFits = (d, cols, rows) => !!d && d.cols === cols && d.rows === rows &&
  d.wallN === WALL_N && d.wallE === WALL_E && d.wallS === WALL_S && d.shelf === SHELF_ROWS;

function prepare(scene, tiles) {
  const root = scene.clone(true);
  const [floor, floorN, brick, brickN] = tiles;
  const meshes = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  meshes.forEach((o) => {
    const m = o.material, name = m.name || "";
    const ao = m.map || null;         // the baked atlas arrives as base colour (UV1)
    if (ao) { ao.colorSpace = THREE.NoColorSpace; ao.needsUpdate = true; }
    let mat;
    const N = new THREE.Vector2(0.7, 0.7);
    if (name === "plaza_floor") mat = new THREE.MeshStandardMaterial({ map: floor, normalMap: floorN, normalScale: N, aoMap: ao, roughness: 0.9 });
    else if (name === "plaza_wall") mat = new THREE.MeshStandardMaterial({ map: brick, normalMap: brickN, normalScale: N, aoMap: ao, roughness: 0.9 });
    else if (name === "plaza_stone") mat = new THREE.MeshStandardMaterial({ ...STONE, aoMap: ao });
    else if (name === "plaza_iron") mat = new THREE.MeshStandardMaterial({ ...IRON, aoMap: ao });
    else mat = new THREE.MeshStandardMaterial({ color: "#0d0914", roughness: 1 });   // the underside
    m.dispose();
    o.material = mat;
    const shell = name !== "plaza_dark";
    o.castShadow = shell && name !== "plaza_floor";
    o.receiveShadow = shell;
  });
  return root;
}

function ShellModel({ cols, rows }) {
  const gltf = useLoader(GLTFLoader, PLAZA_SHELL_URL);
  const raw = useLoader(THREE.TextureLoader, STONE_TILES);
  const tiles = useMemo(() => raw.map((t, i) => tiled(t, i % 2 === 0)), [raw]);
  const dims = useMemo(() => readDims(gltf.scene), [gltf]);
  const fits = shellFits(dims, cols, rows);
  const root = useMemo(() => (fits ? prepare(gltf.scene, tiles) : null), [gltf, fits, tiles]);
  if (!fits) {
    console.warn("[plaza] plaza_shell.glb was built for", dims, "but the Plaza is", { cols, rows }, "— <Room/> stands in; rebuild the shell");
    return <Room cols={cols} rows={rows} />;
  }
  return <primitive object={root} />;
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[plaza] plaza_shell.glb did not load — <Room/> stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

export function SculptedRoom({ cols, rows }) {
  const painted = usePaintedBodies();
  const fb = <Room cols={cols} rows={rows} />;
  if (painted) return fb;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}>
        <ShellModel cols={cols} rows={rows} />
      </Suspense>
    </GlbBoundary>
  );
}
export default SculptedRoom;
